import React, { useState } from "react";
import {
  Box,
  Card,
  CardBody,
  Text,
  HStack,
  VStack,
  SimpleGrid,
  Spinner,
  Switch,
  IconButton,
  Icon,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Portal,
  keyframes,
  Divider,
  Tag,
  Center,
} from "@chakra-ui/react";
import { DeleteIcon, CopyIcon } from "@chakra-ui/icons";
import { FaPlay } from "react-icons/fa";
import { BsThreeDotsVertical } from "react-icons/bs";
import { Header } from "build-ts/metadata";
import { setScheduledJobIsActive, deleteScheduledJob, runJobAsync, getScheduledJob, putScheduledJob } from "services/ScheduleServices";
import showToast from "hooks/useCustomToast";
import { MetadataTypeEnum, metadataTypeEnumToJSON } from "build-ts/enums";
import { ScheduledJobProto, ScheduledScenarioJobProto } from "build-ts/schedule";
import { v4 as uuidv4 } from "uuid";
import { epochConverterWithTimeZone } from "utils/dateUtils";
import { useUser } from "context/UserContext";
import { LuWorkflow } from "react-icons/lu";

const pulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(72, 187, 120, 0.6); }
  70%  { box-shadow: 0 0 0 5px rgba(72, 187, 120, 0); }
  100% { box-shadow: 0 0 0 0 rgba(72, 187, 120, 0); }
`;

interface JobListProps {
  jobs: Header[];
  isLoadingJobs: boolean;
  onJobClick?: (job: Header) => void;
  onRefresh?: () => void;
}

const JobList: React.FC<JobListProps> = ({
  jobs,
  isLoadingJobs,
  onJobClick,
  onRefresh,
}) => {
  const { defaultTimeZone, userId } = useUser();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState<Header | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [runningJobId, setRunningJobId] = useState<string | null>(null);
  const [copyingJobId, setCopyingJobId] = useState<string | null>(null);

  const handleToggleActive = async (job: Header, isActive: boolean) => {
    if (!job.id) return;

    const response = await setScheduledJobIsActive(job.id, isActive);
    if (response) {
      showToast(
        isActive ? "Analysis set to Live" : "Analysis paused",
        `"${job.name}" is now ${isActive ? "live" : "paused"}.`,
        "success"
      );
      onRefresh?.();
    }
  };

  const handleRunJob = async (job: Header) => {
    if (!job.id) return;

    setRunningJobId(job.id);
    await runJobAsync(job.id, metadataTypeEnumToJSON(MetadataTypeEnum.ANALYST));
    setRunningJobId(null);
    showToast("Analysis running", "The analysis has been triggered and is now running.", "success");
    onRefresh?.();
  };

  const handleCopyJob = async (job: Header) => {
    if (!job.id) return;

    setCopyingJobId(job.id);

    try {
      // Fetch the full job details
      const jobDetails = await getScheduledJob(job.id, false);

      if (!jobDetails) {
        showToast("Failed to fetch job details", "", "error");
        setCopyingJobId(null);
        return;
      }

      const currentDarwinName = jobDetails.scheduled_analysis?.darwin_name?.trim() || "";
      const randomSuffix = Math.random().toString(36).slice(2, 8);
      const nextDarwinName = `${currentDarwinName.trim()}_${randomSuffix}`;
      const duplicatedScheduledAnalysis = ScheduledScenarioJobProto.create({
        ...jobDetails.scheduled_analysis,
        darwin_name: nextDarwinName,
      });

      // Create a new job with copied details
      const newJob = ScheduledJobProto.create({
        header: Header.create({
          id: uuidv4(), // Generate new ID
          name: `${jobDetails.header?.name || "Untitled"} (Copy)`,
          type: MetadataTypeEnum.SCHEDULED_JOB,
          owner_id: jobDetails.header?.owner_id,
        }),
        cron_expr: jobDetails.cron_expr,
        type: jobDetails.type,
        is_active: false,
        scheduled_analysis: duplicatedScheduledAnalysis,
      });
      const response = await putScheduledJob(newJob);
      if (response) {
        showToast("Analysis duplicated", `"${newJob.header?.name}" has been created.`, "success");
        onRefresh?.();
      } else {
        showToast("Failed to duplicate", "", "error");
      }
    } catch (error) {
      showToast("Failed to duplicate", "", "error");
    } finally {
      setCopyingJobId(null);
    }
  };

  const handleDeleteClick = (job: Header) => {
    setJobToDelete(job);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!jobToDelete?.id) return;
    setIsDeleting(true);
    const response = await deleteScheduledJob(jobToDelete.id);
    setIsDeleting(false);
    if (response) {
      showToast("Analysis deleted", `"${jobToDelete.name}" has been deleted.`, "success");
      setDeleteModalOpen(false);
      setJobToDelete(null);
      onRefresh?.();
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setJobToDelete(null);
  };

  const yourJobs = jobs.filter((j) => j.author_id === userId);
  const othersJobs = jobs.filter((j) => j.author_id !== userId);

  const renderCard = (job: Header) => {
    const isLive = !!job.has_active_schedule;
    return (
      <Card
        key={job.id}
        cursor="pointer"
        _hover={{ shadow: "md", borderColor: isLive ? "purple.600" : "purple.300", bg: "purple.50"}}
        transition="all 0.2s"
        border="1px solid"
        borderColor={isLive ? "purple.600" : "gray.200"}
        borderLeft="4px solid"
        borderLeftColor={isLive ? "purple.600" : "gray.300"}
        bg="white"
        borderRadius="md"
        onClick={() => onJobClick?.(job)}
      >
        <CardBody py={4} px={4}>
          <VStack align="stretch" spacing={2}>
            <HStack justify="space-between" align="flex-start" spacing={2}>
              <HStack spacing={2} flex={1} minW={0} align="flex-start">
                <Icon
                  as={LuWorkflow}
                  boxSize={7}
                  color={isLive ? "purple.600" : "gray.400"}
                  flexShrink={0}
                />
                <Text
                  fontSize="md"
                  fontWeight="600"
                  color="gray.800"
                  mb={0}
                  flex={1}
                  noOfLines={2}
                  lineHeight="1.4"
                  textAlign="left"
                >
                  {job.name}
                </Text>
              </HStack>

              <HStack spacing={2} flexShrink={0} align="center" onClick={e => e.stopPropagation()}>
                {isLive && (
                  <HStack
                    spacing={2}
                    border="2px solid"
                    borderColor="green.600"
                    borderRadius="full"
                    px={2}
                    py="2px"
                    bg="green.50"
                  >
                    <Box
                      w="7px"
                      h="7px"
                      borderRadius="full"
                      bg="green.600"
                      flexShrink={0}
                      animation={`${pulse} 1.8s ease-in-out infinite`}
                    />
                    <Text fontSize="xs" fontWeight="800" color="green.700" mb={0} letterSpacing="0.05em">
                      LIVE
                    </Text>
                  </HStack>
                )}

                <Menu placement="bottom-end" autoSelect={false}>
                  <MenuButton
                    as={IconButton}
                    aria-label="Analysis options"
                    icon={<Icon as={BsThreeDotsVertical} boxSize={4} />}
                    size="sm"
                    variant="ghost"
                    colorScheme="gray"
                    onClick={e => e.stopPropagation()}
                  />
                  <Portal>
                    <MenuList minW="190px" fontSize="sm" zIndex="dropdown">
                      <MenuItem
                        closeOnSelect={false}
                        onClick={e => e.stopPropagation()}
                      >
                        <HStack justify="space-between" w="100%">
                          <Text mb={0} fontWeight="500">
                            {isLive ? "Live" : "Paused"}
                          </Text>
                          <Switch
                            size="sm"
                            colorScheme="green"
                            isChecked={isLive}
                            onChange={e => { e.stopPropagation(); handleToggleActive(job, !isLive); }}
                          />
                        </HStack>
                      </MenuItem>
                      <MenuDivider />
                      <MenuItem
                        icon={runningJobId === job.id ? <Spinner size="xs" /> : <FaPlay />}
                        onClick={e => { e.stopPropagation(); handleRunJob(job); }}
                        isDisabled={runningJobId === job.id}
                      >
                        Run Analysis
                      </MenuItem>
                      <MenuItem
                        icon={copyingJobId === job.id ? <Spinner size="xs" /> : <CopyIcon />}
                        onClick={e => { e.stopPropagation(); handleCopyJob(job); }}
                        isDisabled={copyingJobId === job.id}
                      >
                        Duplicate
                      </MenuItem>
                      <MenuDivider />
                      <MenuItem
                        icon={<DeleteIcon color="red.400" />}
                        color="red.500"
                        onClick={e => { e.stopPropagation(); handleDeleteClick(job); }}
                      >
                        Delete
                      </MenuItem>
                    </MenuList>
                  </Portal>
                </Menu>
              </HStack>
            </HStack>

            <VStack spacing={0} align="flex-end">
              {job.author_name && (
                <Text fontSize="sm" color="gray.500" mb={0} fontWeight="600">
                  by {job.author_name}
                </Text>
              )}
              {job.created_date_ms ? (
                <Text fontSize="sm" color="gray.500" mb={0}>
                  {epochConverterWithTimeZone(job.created_date_ms, "d MMM yyyy, h:mm a", defaultTimeZone)}
                </Text>
              ) : null}
            </VStack>
          </VStack>
        </CardBody>
      </Card>
    );
  };

  const renderSectionHeader = (title: string, count: number) => (
    <HStack spacing={3} mb={3}>
      <Text fontSize="sm" fontWeight="700" color="gray.700" mb={0}>
        {title}
      </Text>
      <Tag size="sm" colorScheme="purple" borderRadius="full" fontWeight="600"
        bg="purple.600" color="white">
        {count}
      </Tag>
      <Divider flex="1" borderColor="gray.200" />
    </HStack>
  );

  return (
    <Box height="calc(100% - 60px)" overflowY="auto">
      {isLoadingJobs ? (
        <Box height="100%" display="flex" alignItems="center" justifyContent="center">
          <Spinner size="sm" color="purple.500" thickness="4px" />
        </Box>
      ) : jobs.length === 0 ? (
        <Box height="100%" display="flex" alignItems="center" justifyContent="center">
          <Text color="gray.500" fontSize="md" mb={0}>
            No jobs match your search. Try different keywords.
          </Text>
        </Box>
      ) : (
        <VStack spacing={6} align="stretch">
          {/* Your Workflows */}
          <Box>
            {renderSectionHeader("Your Workflows", yourJobs.length)}
            {yourJobs.length === 0 ? (
              <Center py={6}>
                <Text color="gray.400" fontSize="sm" mb={0}>
                  Looks like you don't have any analyses, please create a new one to see it here.
                </Text>
              </Center>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                {yourJobs.map(renderCard)}
              </SimpleGrid>
            )}
          </Box>

          {/* Others' Analyses */}
          {othersJobs.length > 0 && (
            <Box>
              {renderSectionHeader("Others' Workflows", othersJobs.length)}
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                {othersJobs.map(renderCard)}
              </SimpleGrid>
            </Box>
          )}
        </VStack>
      )}

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModalOpen} onClose={handleDeleteCancel} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Analysis</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Are you sure you want to delete "{jobToDelete?.name}"? This action cannot be undone.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleDeleteCancel} isDisabled={isDeleting}>
              Cancel
            </Button>
            <Button colorScheme="red" onClick={handleDeleteConfirm} isLoading={isDeleting} loadingText="Deleting...">
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default JobList;
